import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Loader2, Check, UserPlus, Megaphone } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import athleteApi from '@/lib/api'
import { RootState } from '@/store'

interface MarketerInvitation {
  id: number
  status: 'pending' | 'accepted' | 'expired'
  message?: string
  email: string
  inviter: {
    name: string
    avatar?: string
  }
  campaign: {
    name: string
  }
  brand: {
    name: string
    logoUrl?: string
  }
}

const getErrorMessage = (err: any) => {
  const errorData = err.response?.data
  if (typeof errorData?.error === 'string') return errorData.error
  if (typeof errorData?.message === 'string') return errorData.message
  if (typeof errorData === 'string') return errorData
  return 'An unexpected error occurred'
}

const AcceptMarketerInvite = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const currentUser = useSelector((state: RootState) => state.user.currentUser)
  const userLoading = useSelector((state: RootState) => state.user.loading)

  const [invitation, setInvitation] = useState<MarketerInvitation | null>(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) return
    const fetchInvitation = async () => {
      try {
        const response = await athleteApi.get(`/athlete/marketer-invitations/${token}`)
        setInvitation(response.data)
      } catch (err: any) {
        setError(getErrorMessage(err))
      } finally {
        setLoading(false)
      }
    }
    fetchInvitation()
  }, [token])

  const handleAccept = async () => {
    if (!token) return
    setProcessing(true)
    try {
      await athleteApi.post(`/athlete/marketer-invitations/${token}/accept`)
      setAccepted(true)
    } catch (err: any) {
      toast.error(getErrorMessage(err))
      setProcessing(false)
    }
  }

  const handleSignUp = () => {
    // Store the token so EmailConfirmation can redirect back here after verifying
    localStorage.setItem('pendingMarketerInviteToken', token!)
    const params = new URLSearchParams({ invite: token! })
    if (invitation?.email) params.set('email', invitation.email)
    navigate(`/onboarding?${params.toString()}`)
  }

  const handleSignIn = () => {
    localStorage.setItem('pendingMarketerInviteToken', token!)
    navigate(`/signin?returnTo=/marketer-invite/${token}`)
  }

  if (loading || userLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <Loader2 className="h-8 w-8 animate-spin text-[#BCF565]" />
      </div>
    )
  }

  if (error || !invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black p-4">
        <Card className="w-full max-w-md bg-[#1a1a1a] border-white/10 text-white">
          <CardHeader>
            <CardTitle className="text-red-500">Invitation Error</CardTitle>
            <CardDescription className="text-gray-400">
              {error || 'Invitation not found or invalid.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full bg-white/10 hover:bg-white/20 text-white"
              onClick={() => navigate('/signin')}
            >
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (invitation.status !== 'pending') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black p-4">
        <Card className="w-full max-w-md bg-[#1a1a1a] border-white/10 text-white">
          <CardHeader>
            <CardTitle>
              Invitation {invitation.status === 'accepted' ? 'Already Accepted' : 'Expired'}
            </CardTitle>
            <CardDescription className="text-gray-400">
              This invitation is no longer valid or has already been used.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full bg-[#BCF565] text-black hover:bg-[#a8e055]"
              onClick={() => navigate(currentUser ? '/' : '/signin')}
            >
              {currentUser ? 'Go to Dashboard' : 'Go to Sign In'}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Post-acceptance confirmation screen
  if (accepted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black p-4 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#BCF565]/5 rounded-full blur-3xl pointer-events-none" />
        <Card className="w-full max-w-lg bg-[#1a1a1a]/90 backdrop-blur-xs border-white/10 text-white relative z-10 text-center">
          <CardContent className="pt-10 pb-10 space-y-4">
            <div className="mx-auto bg-[#BCF565]/10 p-4 rounded-full w-fit">
              <Check className="h-10 w-10 text-[#BCF565]" />
            </div>
            <h2 className="text-2xl font-bold">You're on Athios!</h2>
            <p className="text-gray-400 max-w-xs mx-auto">
              <strong className="text-white">{invitation.inviter.name}</strong> has been notified
              and will be in touch about the{' '}
              <strong className="text-white">{invitation.campaign.name}</strong> collaboration.
            </p>
            <Button
              className="mt-4 bg-[#BCF565] text-black hover:bg-[#a8e055]"
              onClick={() => navigate('/')}
            >
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const isAuthenticated = !!currentUser

  return (
    <div className="flex min-h-screen items-center justify-center bg-black p-4 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#BCF565]/5 rounded-full blur-3xl pointer-events-none" />

      <Card className="w-full max-w-lg bg-[#1a1a1a]/90 backdrop-blur-xs border-white/10 text-white relative z-10">
        <CardHeader className="text-center pb-6 border-b border-white/5">
          <div className="mx-auto bg-[#BCF565]/10 p-3 rounded-full w-fit mb-4">
            <Megaphone className="h-8 w-8 text-[#BCF565]" />
          </div>
          <CardTitle className="text-2xl font-bold">You've Been Invited!</CardTitle>
          <CardDescription className="text-gray-400 max-w-xs mx-auto mt-2">
            {invitation.inviter.name} wants you on Athios for their collaboration.
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6 space-y-6">
          {/* Inviter */}
          <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg border border-white/5">
            <Avatar className="h-12 w-12 border border-[#BCF565]/20 shrink-0">
              <AvatarImage src={invitation.inviter.avatar} />
              <AvatarFallback className="bg-[#BCF565]/10 text-[#BCF565]">
                {invitation.inviter.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-semibold">{invitation.inviter.name}</div>
              <div className="text-sm text-gray-400">{invitation.brand.name}</div>
            </div>
          </div>

          {/* Collaboration */}
          <div className="p-4 bg-white/5 rounded-lg border border-white/5">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Collaboration</div>
            <div className="font-semibold">{invitation.campaign.name}</div>
          </div>

          {/* Personal message */}
          {invitation.message && (
            <div className="bg-white/5 p-4 rounded-lg border border-white/5 relative">
              <div className="absolute -top-2 left-6 bg-[#1a1a1a] px-2 text-xs text-gray-400">
                Message from {invitation.inviter.name.split(' ')[0]}
              </div>
              <p className="text-gray-300 italic text-sm">"{invitation.message}"</p>
            </div>
          )}

          {/* Actions */}
          {isAuthenticated ? (
            <Button
              className="w-full bg-[#BCF565] text-black hover:bg-[#a8e055] h-12"
              onClick={handleAccept}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Accept
            </Button>
          ) : (
            <div className="space-y-3">
              <Button
                className="w-full bg-[#BCF565] text-black hover:bg-[#a8e055] h-12"
                onClick={handleSignUp}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Create Account
              </Button>
              <Button
                variant="outline"
                className="w-full border-white/10 hover:bg-white/5 text-white h-12"
                onClick={handleSignIn}
              >
                Already have an account? Sign In
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default AcceptMarketerInvite
